import { Button } from '@accure/ui';
import { flatMap, uniq } from 'lodash';
import { Download } from 'lucide-react';
import { usePostHog } from 'posthog-js/react';

import { Tooltip } from '@/components/ui/tooltip';
import { useCurrentAsset } from '@/hooks/use-current-asset';
import { useMe } from '@/hooks/use-me';
import { downloadCsv } from '@/utils/download-csv';

import { Table } from '../../entities/system-logs';
import { cleanFilters } from '../../utils/clean-filters';
import { flattenRows } from '../../utils/flatten-rows';
import { useSystemLogsQueryState } from '../state/use-system-logs-query';

/**
 * Escapes a value for safe CSV output.
 */
function escapeCsvValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  const str = String(value);
  const needsQuotes = /[",\n]/.test(str);
  const escaped = str.replace(/"/g, '""');
  return needsQuotes ? `"${escaped}"` : escaped;
}

/**
 * Converts an array of row objects to CSV, omitting the 'id' column.
 */
function rowsToCsv(rows: Record<string, unknown>[]): string {
  if (rows.length === 0) return '';
  const headers = uniq(
    flatMap(rows, (row) => Object.keys(row).filter((k) => k !== 'id')),
  );
  const lines = [headers.join(',')];
  for (const row of rows) {
    const values = headers.map((h) => escapeCsvValue((row as any)[h]));
    lines.push(values.join(','));
  }
  return lines.join('\n');
}

export type SystemLogsExportButtonProps = {
  table?: Table;
};

/**
 * Handles the export logic for system logs as CSV.
 * Omits the 'id' field from each row to avoid unused variable lint errors.
 */
function downloadTableCsv(table?: Table) {
  const flattenedRows = flattenRows(
    table?.rows ?? [],
    table?.columns ?? [],
  ).map((row) => {
    // Omit 'id' property to avoid unused variable lint error
    const { id: _id, ...rest } = row;
    return rest;
  });
  const csv = rowsToCsv(flattenedRows);
  const timestamp = new Date().toISOString().replace(/[:]/g, '-');
  const fileName = `system-logs_${timestamp}.csv`;
  downloadCsv(csv, fileName);
}

/**
 * SystemLogsExportButton - provides a CSV export for the current table.
 * Tracks export events with PostHog for analytics.
 */
export function SystemLogsExportButton({ table }: SystemLogsExportButtonProps) {
  const hasRows = !!table && table.rows.length > 0;
  const [filters] = useSystemLogsQueryState();
  const posthog = usePostHog();
  const asset = useCurrentAsset();
  const me = useMe();

  const handleExportClick = () => {
    downloadTableCsv(table);

    const cleanedFilters = cleanFilters(filters);
    const rowCount = table?.rows.length ?? 0;
    const isAssetContext = Boolean(asset.asset || asset.vehicle);

    posthog.capture('system_logs_exported', {
      asset_id: asset.accureId,
      asset_url_id: asset.assetId,
      customer: me.tenant,
      row_count: rowCount,
      filters: cleanedFilters,
      is_asset_context: isAssetContext,
      is_mobility: asset.isMobility,
      timestamp: new Date().toISOString(),
    });
  };

  return (
    <Tooltip content="No rows to export" delay hide={hasRows}>
      <Button
        disabled={!hasRows}
        onClick={handleExportClick}
        size="sm"
        data-testid="system-logs-export-button"
        data-ph-capture-attribute-action="export_system_logs"
        data-ph-capture-attribute-asset-id={asset.accureId}
        data-ph-capture-attribute-customer={me.tenant}
        data-ph-capture-attribute-row-count={table?.rows.length ?? 0}
      >
        <Download className="size-4" /> Export CSV
      </Button>
    </Tooltip>
  );
}
